import { useCallback } from 'react';
import { Modal, Stack, Text, Button, Group, Progress } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIdleTimeout, formatRemainingTime } from '../../hooks';
import { useAuthStore } from '../../stores/auth';
import { usePersonaStore } from '../../stores/persona';
import { useImpersonationStore } from '../../stores/impersonation';
import { isMagicLinkVerifyPath } from '../../pages/auth/magicLinkRedirect';

// MED-1298 requires these limits in production.
const DEFAULT_IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const COACH_IDLE_TIMEOUT = 45 * 60 * 1000; // 45 minutes
// MED-1383: on staging and in development, testers keep tabs open for hours,
// and a 30 minute limit logged them out mid-test. Every other environment,
// production included, keeps the limits above.
const NON_PRODUCTION_IDLE_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
const WARNING_TIME = 2 * 60 * 1000; // 2 minutes before logout

// MED-1383: an allow-list, so a missing or unknown value fails closed to the
// production limits. The raw value is read on purpose: env.environment
// turns a missing value into 'development'. Only the local dev server
// (import.meta.env.DEV) with no value set counts as development.
const RAW_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;
const ALLOWS_TEST_LIMITS =
  RAW_ENVIRONMENT === 'staging' ||
  RAW_ENVIRONMENT === 'development' ||
  (import.meta.env.DEV && !RAW_ENVIRONMENT);

function defaultIdleTimeout(isCoachPortal: boolean): number {
  if (ALLOWS_TEST_LIMITS) return NON_PRODUCTION_IDLE_TIMEOUT;
  return isCoachPortal ? COACH_IDLE_TIMEOUT : DEFAULT_IDLE_TIMEOUT;
}

/**
 * MED-1298 (QA): reproducing the warning naturally takes 28 minutes of
 * waiting. On staging and in development, the durations can be overridden from the
 * console, e.g.:
 *   localStorage.setItem('medley_idle_timeout_override_ms', '30000')
 *   localStorage.setItem('medley_idle_warning_override_ms', '20000')
 * then reload. Remove the keys (or clear storage) to restore the real values.
 * Ignored entirely outside the allow-list above, so AC13's durations are
 * unchanged in production.
 */
function readDurationOverrideMs(key: string): number | null {
  if (!ALLOWS_TEST_LIMITS) return null;
  try {
    const parsed = parseInt(localStorage.getItem(key) ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

interface IdleTimeoutProviderProps {
  children: React.ReactNode;
}

export function IdleTimeoutProvider({ children }: IdleTimeoutProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuthStore();
  const { resetToDefault } = usePersonaStore();
  // MED-1383: the base user of this tab, for the cross-tab logout signal.
  const originalUserId = useImpersonationStore(state => state.originalUser?.id);
  const baseUserId = originalUserId ?? user?.id ?? null;

  const isCoachPortal = location.pathname.startsWith('/coach');
  const idleTimeout =
    readDurationOverrideMs('medley_idle_timeout_override_ms') ?? defaultIdleTimeout(isCoachPortal);
  const warningTime =
    readDurationOverrideMs('medley_idle_warning_override_ms') ?? WARNING_TIME;

  const handleTimeout = useCallback(async () => {
    // Skip if auth is already cleared (e.g., API client already redirecting on 401)
    if (!useAuthStore.getState().isAuthenticated) return;
    const loginPath = window.location.pathname.startsWith('/coach') ? '/coach/login' : '/login';
    resetToDefault();
    await logout();
    navigate(loginPath, { replace: true });
  }, [logout, navigate, resetToDefault]);

  const { isWarning, remainingTime, resetTimer } = useIdleTimeout({
    timeout: idleTimeout,
    warningTime,
    onTimeout: handleTimeout,
    onWarning: () => {
      // Optional: Could play a sound or show a notification
    },
    enabled: isAuthenticated,
    userId: baseUserId,
    // MDF-258: a timeout here sent the user to /login and lost the link's
    // token. Pausing keeps the idle period, so an idle session still ends on
    // the next page unless the link signs the user in again.
    paused: isMagicLinkVerifyPath(location.pathname),
  });

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  const handleLogoutNow = async () => {
    const loginPath = window.location.pathname.startsWith('/coach') ? '/coach/login' : '/login';
    resetToDefault();
    await logout();
    navigate(loginPath, { replace: true });
  };

  // Calculate progress percentage for the warning period
  const progressPercent = Math.max(0, (remainingTime / warningTime) * 100);

  return (
    <>
      {children}

      <Modal
        opened={isWarning && isAuthenticated}
        onClose={handleStayLoggedIn}
        title="Session Timeout Warning"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
      >
        <Stack gap="lg">
          <Text size="sm">
            Your session will expire due to inactivity. You will be logged out in:
          </Text>

          <Text size="xl" fw={700} ta="center" c="dangerRed">
            {formatRemainingTime(remainingTime)}
          </Text>

          <Progress value={progressPercent} color="dangerRed" size="sm" animated />

          <Text size="sm" c="dimmed">
            Click "Stay Logged In" to continue your session, or "Log Out Now" to end your session
            immediately.
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleLogoutNow}>
              Log Out Now
            </Button>
            <Button onClick={handleStayLoggedIn}>Stay Logged In</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
